import { useNavigate } from 'react-router-dom';

import React from 'react';
import { reverse } from 'named-urls';
import { GridEventListener, GridRowParams, GridValidRowModel } from '@mui/x-data-grid';

import { VatixError } from 'components/Error/types';

import routes from 'core/routes';

import Grid, { defaultFormatters, defaultOperators, defaultRenderers } from 'components/Grid';
import { ColumnDefinition, FilterType } from 'components/Grid/types';

import { ListRendererType } from 'components/Grid/renderers/GridListRenderer/types';
import { listRenderer } from 'components/Grid/renderers/GridListRenderer';

import Search from 'components/Grid/operators/Search';
import API from 'utils/api/api';

import { EmptyValue } from 'components/SharedFormComponents/CustomFieldDisplayValue/styles';

import DeviceTypeCell from 'containers/Devices/components/DeviceTypeCell';
import { formatMoment } from 'utils/formatters/timezone';
import { DATETIME_COLUMN_MIN_WIDTH, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR } from 'core/constants';
import { Container as GridChipContainer } from 'components/Grid/renderers/GridChipRenderer/styles';

import Chip from 'components/Chip';

import { colors } from 'theme/colors';

import { StyledCreated, Container } from './styles';

const basicColumns = [
  {
    field: 'type',
    headerName: 'Device',
    filterOperators: Search(API.devices.searchDevices, API.devices.loadDeviceDetails, 'customId'),
    renderCell: DeviceTypeCell,
    valueGetter: (value: unknown, row: GridValidRowModel) => ({ type: value, customId: row.customId }),
    nativeFilter: 'devices',
    // ToDo: implement sorting on backend
    sortable: false,
  },
  {
    field: 'tags',
    headerName: 'User tags',
    filterOperators: Search(API.devices.searchDeviceTags, API.devices.deviceTagsDetails, 'name'),
    renderCell: listRenderer(
      ((props) => (
        <GridChipContainer>
          <Chip
            // @ts-ignore
            label={props.value.value.name}
            size="small"
            variant="filled"
            colors={{ background: colors.tint.primary.light, foreground: colors.primary.main }}
          />
        </GridChipContainer>
      )) as ListRendererType,
      3
    ),
    nativeFilter: 'device_tags',
    sortable: false,
  },
  {
    field: 'user',
    headerName: 'User',
    filterOperators: defaultOperators.user(),
    renderCell: defaultRenderers.user ? defaultRenderers.user : undefined,
    valueFormatter: defaultFormatters.user,
    valueGetter: (value: unknown) => ({ value }),
    nativeFilter: 'users',
    // ToDo: implement sorting on backend
    sortable: false,
  },
  {
    field: 'lastPoll',
    filterOperators: defaultOperators.date_time(),
    headerName: 'Last online',
    minWidth: DATETIME_COLUMN_MIN_WIDTH,
    renderCell: ({ value }: { value: string }) =>
      value ? (
        <StyledCreated data-testid="usage-last-online">
          {formatMoment(value, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}
        </StyledCreated>
      ) : (
        <EmptyValue>-</EmptyValue>
      ),
    nativeFilter: true,
    valueFormatter: defaultFormatters.date_time,
    sortingField: 'last_poll',
  },
] as unknown as ColumnDefinition[];

const DeviceList: React.FunctionComponent<{
  onError: (error: VatixError) => void;
  filters: FilterType[];
  onRefreshCallback: (callback: () => void) => void;
}> = ({ onError, filters, onRefreshCallback }): React.ReactElement => {
  const navigate = useNavigate();
  const onRowClick: GridEventListener<'rowClick'> = (task: GridRowParams): void => {
    navigate(reverse(routes.dashboard.alarms.devices.details, { deviceId: task.row.uuid }));
  };

  return (
    <Container>
      <Grid
        entity="device"
        onError={onError}
        disableColumnSelector
        temporaryFilters={filters}
        onRowClick={onRowClick}
        basicColumns={basicColumns}
        onRefreshCallback={onRefreshCallback}
        dataURL="/api/reports/usage/table/?type=device"
      />
    </Container>
  );
};

export default DeviceList;
